// HollowScript tokenisation for Monaco, driven by the real lexer via
// `@hollow/script`'s published `highlight()` - not a second, hand-written
// grammar. This is a direct port of Cascade's own bridge
// (`packages/ui/src/languages/hollowscript.ts`, `hollowScriptParser`) from
// CodeMirror's `StreamParser` shape to a per-line array of tokens; the
// underlying trick is identical and copied from that file's own doc
// comment, reproduced here because it's the reason this design is safe:
//
//   `highlight()` is a whole-document batch tokeniser, but every
//   HollowScript token is confined to a single line (spec.md §1.4, §1.5
//   both make a raw newline inside a string or filter literal a lex
//   error), so re-lexing just the current line's text through the real
//   lexer reproduces the exact same classification a whole-document lex
//   would give for everything except a multi-line expression embedded
//   inside a string interpolation `${ ... }` - the one construct whose
//   *content* can legitimately cross a line boundary. That rare case
//   degrades to imperfect highlighting on the affected lines only; it
//   never touches what the real checker or interpreter do.
//
// Returns plain data (no Monaco types anywhere in this module) so it stays
// unit-testable with zero editor dependency and so the ~98MB `monaco-editor`
// package is never pulled into this module's own import graph - only the
// thin Studio-layer adapter that actually builds a
// `monaco.languages.TokensProvider` needs to know Monaco's shapes at all
// (`src/studio/editor/scripting/monaco/monacoAdapter.ts`).

import { highlight } from '@hollow/script';
import type { HighlightClass } from '@hollow/script';

export interface HollowScriptToken {
  /** 0-based character offset into the line where this token's styling
   * begins; continues until the next token's `startIndex` (or the end of
   * the line). */
  readonly startIndex: number;
  /** `null` for an unstyled gap (whitespace, or an untagged comment body
   * character run between tagged spans) - `highlight()` only returns spans
   * for real tokens and tagged-comment text, never for insignificant gaps,
   * matching Cascade's own `hollowScriptParser` behaviour for the same
   * case. */
  readonly kind: HighlightClass | null;
}

/** Tokenises one line of HollowScript source (no trailing newline) into a
 * gap-filled, monotonically-increasing-`startIndex` token list - the shape
 * every line-based tokeniser (CodeMirror's `StreamParser`, Monaco's
 * `TokensProvider`) ultimately wants, decoupled from either's actual API.
 * Always starts at index 0 (even when the line begins mid-gap) and never
 * throws (`highlight` itself never does, spec.md gate 2). */
export function tokenizeHollowScriptLine(line: string): readonly HollowScriptToken[] {
  const spans = highlight(line);
  // A blank or pure-whitespace line (no real tokens at all) is exactly one
  // unstyled gap covering the whole line - handled separately so the
  // fallback below and the trailing-gap push after the loop can never both
  // fire for the same, already-zero cursor position.
  if (spans.length === 0) return [{ startIndex: 0, kind: null }];

  const tokens: HollowScriptToken[] = [];
  let cursor = 0;
  for (const { span, className } of spans) {
    if (span.start > cursor) tokens.push({ startIndex: cursor, kind: null });
    tokens.push({ startIndex: span.start, kind: className });
    cursor = span.end;
  }
  if (cursor < line.length) tokens.push({ startIndex: cursor, kind: null });
  return tokens;
}
